import { Box, Typography } from "@mui/material";
import React, { useRef } from "react";
import ScatterGraph from "./ScatterGraph";
import "./DataVisualizer.css";
import { useSelector } from "react-redux";
import { tokens, monoFont, montserratFont } from "../../theme";

export default function DataVisualizer(props) {
    const laserData = useSelector(state => state.laserData);
    const canvasRefXY = useRef(null);
    const canvasRefXZ = useRef(null);
    const {reset, onRangeUpdated} = props;

    const onDataSelected = (start, end) => {
        if(onRangeUpdated) {
            onRangeUpdated(start, end);
        }
    }

    // tokens.machine/motor (theme.js) - shared with Raw Surface's range bands, since both
    // encode which shaft something belongs to, not whether it was accepted.
    const MACHINE_COLOR = tokens.machine;
    const MOTOR_COLOR = tokens.motor;

    const graphOptions = (title) => {

        return{
            type: "scatter",
            data: {
                datasets: [
                    {
                        label: 'Machine (Filtered)',
                        data: [],
                        borderColor: tokens.ink,
                        backgroundColor: MACHINE_COLOR,
                        pointStyle: "rectRot",
                        radius: 6,
                    },
                    {
                        label: 'Motor (Filtered)',
                        data: [],
                        borderColor: tokens.ink,
                        backgroundColor: MOTOR_COLOR,
                        pointStyle: "rectRot",
                        radius: 6,
                    },
                    {
                        label: 'Machine',
                        data: [],
                        backgroundColor: 'rgba(196,60,52,.5)'
                    },
                    {
                        label: 'Motor',
                        data: [],
                        backgroundColor: 'rgba(52,142,95,.5)'
                    },
                    {
                        label: 'All Data',
                        data: [],
                        backgroundColor: tokens.muted2,
                    }
                ]
            },
            options: {
                //Customize chart options
                normalized: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 0
                },
                select: {
                    enabled: true,
                    onDataSelected: onDataSelected
                },
                // Chart.js can't read the MUI theme, so its own defaults (dark grey text/grid,
                // meant for a light canvas) go almost invisible against the dark chart-container
                // background - these have to be set explicitly to the same tokens as everything
                // else on the page.
                scales: {
                    x: {
                        ticks: { color: tokens.muted, font: { family: monoFont } },
                        grid: { color: tokens.line },
                        title: { color: tokens.muted },
                    },
                    y: {
                        ticks: { color: tokens.muted, font: { family: monoFont } },
                        grid: { color: tokens.line },
                        title: { color: tokens.muted },
                    },
                },
                plugins: {
                    title: {
                        display: true,
                        text: title,
                        color: tokens.textSoft,
                        font: { family: montserratFont, weight: 700, size: 13 },
                    },
                    // No legend plugin config - the Legend plugin isn't registered at all
                    // (ScatterGraph.js); the shared, hand-styled legend row below both charts is
                    // the only legend, matching the mockup rather than chart.js's own.
                }
            }
        };
    }

    return (
        <Box>
            <Box className="chart-container chart-container-gutter" >
                <ScatterGraph
                    data={[
                        laserData.filteredY.machine, 
                        laserData.filteredY.motor, 
                        laserData.rangeY.machine,
                        laserData.rangeY.motor,
                        laserData.YValues
                    ]}
                    reset={reset}
                    content={
                    <canvas 
                        id="chartXY" 
                        ref={canvasRefXY} 
                        className="graph-canvas"
                    />}
                    options={graphOptions("Vertical Values")}
                />
            </Box>
            <Box className="chart-container" >
                <ScatterGraph
                        data={[
                            laserData.filteredZ.machine, 
                            laserData.filteredZ.motor, 
                            laserData.rangeZ.machine,
                            laserData.rangeZ.motor,
                            laserData.ZValues
                        ]}
                        reset={reset}
                        content={
                        <canvas 
                            id="chartXZ" 
                            ref={canvasRefXZ} 
                            className="graph-canvas"
                        />}
                        options={graphOptions("Horizontal Values")}
                    />
            </Box>

            {/* Shared legend + points caption, once for both charts - matches the mockup's
                .legend/.decim-caption rows rather than each chart carrying its own. */}
            <Box sx={{
                display: "flex", gap: "14px", alignItems: "center",
                fontSize: "11.5px", color: tokens.muted, padding: "2px 4px",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: MACHINE_COLOR }} />
                    Machine
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: MOTOR_COLOR }} />
                    Motor
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: tokens.muted2 }} />
                    All data
                </Box>
                <Typography sx={{ marginLeft: "auto", color: tokens.muted2, fontSize: "inherit" }}>
                    Drag across a chart to select a machine/motor range
                </Typography>
            </Box>
            <Typography sx={{ textAlign: "center", fontSize: "11.5px", color: tokens.muted, padding: "2px 4px 0" }}>
                Machine{" "}
                <Box component="span" sx={{ color: tokens.textSoft }}>{laserData.filteredY.machine.length}</Box>
                {" "}pts &middot; Motor{" "}
                <Box component="span" sx={{ color: tokens.textSoft }}>{laserData.filteredY.motor.length}</Box>
                {" "}pts
            </Typography>
        </Box>
    );
}